import { createGlobalStyle } from 'styled-components';

/**
 * Estilos globais na identidade Palmer.
 *
 * A fonte Nunito Sans é auto-hospedada (import em main.tsx), não vem mais do
 * Google Fonts: elimina render-block, dependência externa e transferência de IP
 * do usuário para terceiro.
 *
 * Este arquivo absorveu o antigo `assets/styles/global.css`: havia duas fontes
 * de verdade concorrentes para o mesmo escopo.
 */
export const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html, body {
    font-family: ${props => props.theme.typography.fontFamily};
    /* 14px: o AntD já renderiza tudo em 14: manter o body em 16 criava duas
       escalas brigando entre componentes AntD e styled-components. */
    font-size: ${props => props.theme.typography.fontSize.sm};
    line-height: ${props => props.theme.typography.lineHeight.normal};
    color: ${props => props.theme.colors.textPrimary};
    background-color: ${props => props.theme.colors.bgSecondary};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    /* Sem itálico sintético: a Nunito Sans variável não traz eixo de itálico. */
    font-synthesis: none;
    text-rendering: optimizeLegibility;
  }

  #root {
    min-height: 100vh;
  }

  /* Links */
  a {
    color: ${props => props.theme.colors.link};
    text-decoration: none;
    transition: color ${props => props.theme.transitions.fast};

    &:hover {
      color: ${props => props.theme.colors.linkHover};
    }
  }

  /* Foco visível: navy, consistente com o halo dos inputs. */
  :focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }

  /* Scrollbar personalizada */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.bgTertiary};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.gray[300]};
    border-radius: ${props => props.theme.borderRadius.sm};

    &:hover {
      background: ${props => props.theme.colors.gray[400]};
    }
  }

  /* ============================================================
   * Ant Design: ajustes globais
   * ============================================================ */

  .ant-table-wrapper .ant-table-thead > tr > th {
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
  }

  /* Dígitos alinhados em coluna: a Nunito Sans usa dígitos proporcionais por
     padrão, sem isso colunas de nota/valor não alinham verticalmente. */
  .ant-table-cell-number,
  .ant-statistic-content-value,
  .ant-pagination,
  .tabular-nums {
    font-variant-numeric: tabular-nums;
  }

  .ant-btn-primary {
    box-shadow: none;
  }

  .ant-menu-inline {
    border-inline-end: none !important;
  }

  /* ============================================================
   * Body scroll lock quando Modal ou Drawer AntD está aberto.
   *
   * Por que: o app usa containers internos com scroll próprio
   * (#root, ant-layout-content). O AntD trava body{overflow:hidden}
   * ao abrir uma modal, mas isso não afeta os containers internos.
   * As regras abaixo cascateiam o lock usando :has().
   * ============================================================ */
  html:has(.ant-modal-mask),
  html:has(.ant-drawer-mask),
  html:has(.ant-modal-mask) body,
  html:has(.ant-drawer-mask) body,
  html:has(.ant-modal-mask) #root,
  html:has(.ant-drawer-mask) #root,
  html:has(.ant-modal-mask) .ant-layout,
  html:has(.ant-drawer-mask) .ant-layout,
  html:has(.ant-modal-mask) .ant-layout-content,
  html:has(.ant-drawer-mask) .ant-layout-content {
    overflow: hidden !important;
    overscroll-behavior: contain !important;
  }

  .ant-modal-body,
  .ant-drawer-body {
    overscroll-behavior: contain;
  }

  /* ============================================================
   * Modal AntD: padrão global de tamanho
   *
   * Garante que toda modal caiba na viewport, com header e footer
   * sempre visíveis e body rolando internamente.
   * ============================================================ */
  .ant-modal .ant-modal-content {
    max-height: calc(100vh - 40px);
    display: flex !important;
    flex-direction: column;
    padding: 0;
    overflow: hidden;
  }

  .ant-modal .ant-modal-header {
    flex: 0 0 auto;
    margin: 0;
    padding: 16px 24px;
    border-bottom: 1px solid ${props => props.theme.colors.borderSecondary};
  }

  .ant-modal .ant-modal-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 20px 24px;
  }

  .ant-modal .ant-modal-footer {
    flex: 0 0 auto;
    margin: 0;
    padding: 12px 24px;
    border-top: 1px solid ${props => props.theme.colors.borderSecondary};
  }

  .ant-modal .ant-modal-close {
    top: 12px;
    inset-inline-end: 16px;
  }

  /* ============================================================
   * Utilitários
   * ============================================================ */

  .text-center { text-align: center; }
  .text-right  { text-align: right; }

  .flex-center {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .flex-between {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  /* Toda tabela/grade larga rola em si mesma, nunca empurra a página. */
  .tabela-rolavel {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .mb-0 { margin-bottom: 0; }
  .mb-1 { margin-bottom: 8px; }
  .mb-2 { margin-bottom: 16px; }
  .mb-3 { margin-bottom: 24px; }
  .mb-4 { margin-bottom: 32px; }

  .mt-0 { margin-top: 0; }
  .mt-1 { margin-top: 8px; }
  .mt-2 { margin-top: 16px; }
  .mt-3 { margin-top: 24px; }
  .mt-4 { margin-top: 32px; }

  /* ============================================================
   * Movimento reduzido: respeita a preferência do sistema.
   * ============================================================ */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;
